import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { Icon, Button } from "semantic-ui-react";

const OVERLAY_VERSION = "2026.05.20_sophia_clients_region_v6"; // Cambiar esto para forzar que aparezca de nuevo

const OverlayContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(10, 18, 38, 0.4);
  backdrop-filter: blur(12px);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const GlassCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  border-radius: 24px;
  max-width: 600px;
  width: 100%;
  padding: 40px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(168, 85, 247, 0.05) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
`;

const Header = styled.div`
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-family: 'Outfit', 'Inter', sans-serif;
  font-size: 2.2rem;
  font-weight: 800;
  color: white;
  margin-bottom: 10px;
  background: linear-gradient(to right, #fff, #a855f7);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.5);
  font-size: 1.1rem;
`;

const FeatureList = styled.div`
  text-align: left;
  margin: 40px 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FeatureItem = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: 15px;
  background: rgba(255, 255, 255, 0.03);
  padding: 15px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const FeatureIcon = styled.div`
  width: 40px;
  height: 40px;
  background: rgba(168, 85, 247, 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a855f7;
  font-size: 1.2rem;
`;

const FeatureText = styled.div`
  flex: 1;
`;

const FeatureTitle = styled.h3`
  color: white;
  font-size: 1rem;
  margin: 0 0 5px 0;
  font-weight: 600;
`;

const FeatureDesc = styled.p`
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.85rem;
  margin: 0;
  line-height: 1.4;
`;

const Footer = styled.div`
  margin-top: 20px;
`;

const ActionButton = styled(Button)`
  &&& {
    background: linear-gradient(135deg, #a855f7 0%, #6366f1 100%) !important;
    color: white !important;
    padding: 14px 40px !important;
    border-radius: 12px !important;
    font-weight: 700 !important;
    font-size: 1rem !important;
    box-shadow: 0 10px 20px -5px rgba(168, 85, 247, 0.3) !important;
    transition: all 0.3s ease !important;

    &:hover {
      transform: translateY(-2px) !important;
      box-shadow: 0 15px 25px -5px rgba(168, 85, 247, 0.4) !important;
    }
  }
`;

const UpdateOverlay = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const lastSeen = localStorage.getItem("mapsales_update_seen");
    if (lastSeen !== OVERLAY_VERSION) {
      // Pequeño delay para que no aparezca de golpe al cargar
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    localStorage.setItem("mapsales_update_seen", OVERLAY_VERSION);
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <OverlayContainer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <GlassCard
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <Content>
              <Header>
                <Title>Sophia IA — Mayo 2026</Title>
                <Subtitle>Análisis de clientes por zona, caché instantáneo y nueva voz experta.</Subtitle>
              </Header>

              <FeatureList>
                <FeatureItem
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <FeatureIcon><Icon name="map marker alternate" /></FeatureIcon>
                  <FeatureText>
                    <FeatureTitle>Clientes por Región Geográfica</FeatureTitle>
                    <FeatureDesc>
                      Pregúntale a Sophia qué socios comerciales hay en cualquier zona — corregimiento, distrito o barrio. Obtendrás la lista completa con canal, categoría y ventas, más un análisis estratégico basado en los datos reales de ese territorio.
                    </FeatureDesc>
                  </FeatureText>
                </FeatureItem>

                <FeatureItem
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <FeatureIcon><Icon name="bolt" /></FeatureIcon>
                  <FeatureText>
                    <FeatureTitle>Análisis de Trade Area Instantáneo</FeatureTitle>
                    <FeatureDesc>
                      Las isócronas de tus socios ahora están precalculadas. Los análisis de áreas de influencia y canibalización que antes tardaban hasta 45 segundos, ahora se resuelven en menos de 50 ms.
                    </FeatureDesc>
                  </FeatureText>
                </FeatureItem>

                <FeatureItem
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  <FeatureIcon><Icon name="chart line" /></FeatureIcon>
                  <FeatureText>
                    <FeatureTitle>Sophia, Analista de Retail y Distribución</FeatureTitle>
                    <FeatureDesc>
                      Sophia ya no da respuestas genéricas. Lee los datos reales de cada zona y te dice lo que importa: clientes A sin ventas, canales dormidos, oportunidades de Trade Marketing — razonando desde los números, no desde una plantilla.
                    </FeatureDesc>
                  </FeatureText>
                </FeatureItem>
              </FeatureList>

              <Footer>
                <ActionButton onClick={handleClose}>
                  ENTENDIDO
                </ActionButton>
              </Footer>
            </Content>
          </GlassCard>
        </OverlayContainer>
      )}
    </AnimatePresence>
  );
};

export default UpdateOverlay;
